import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Phone, Globe2 } from "lucide-react";
import SEO from "../components/SEO";
import LeadForm from "../components/LeadForm";
import { Chapter, MaskedLine } from "../components/Reveal";
import { ORG_ID } from "../data/schema";
import { waLink, CONTEXT_MESSAGES, WA_NUMBER, WA_NUMBER_DISPLAY } from "../lib/whatsapp";
import { track } from "../lib/analytics";

export default function Contact() {
  const [sent, setSent] = useState(null);

  return (
    <main className="pt-36 lg:pt-44" data-testid="contact-page">
      <SEO
        path="/contact"
        schema={{ "@type": "ContactPage", "@id": "https://grotechweb.com/contact/#contactpage", url: "https://grotechweb.com/contact/", name: "Contact Grotech Web", about: { "@id": ORG_ID } }}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]}
      />
      <section className="mx-auto grid max-w-7xl gap-12 px-4 pb-24 sm:px-8 lg:grid-cols-2 lg:px-16">
        <div>
          <Chapter index="—" label="Contact" />
          <h1 className="mt-8 font-display text-4xl font-black leading-[1.04] tracking-tight sm:text-5xl lg:text-6xl">
            <MaskedLine>Let's Talk About Your Website</MaskedLine>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-white/60 sm:text-lg">
            Tell us what your business does and what you need: a new website, a redesign, SEO, a Shopify store or an AI chatbot. You get a custom plan with a fixed price in your currency.
          </p>
          <ul className="mt-10 space-y-5">
            <li>
              <a
                href={waLink(CONTEXT_MESSAGES.general)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("whatsapp_click", { location: "contact_page" })}
                className="flex items-center gap-4 rounded-2xl border border-[#25D366]/40 p-5 transition-colors hover:bg-[#25D366]/10"
                data-testid="contact-whatsapp"
              >
                <MessageCircle className="h-6 w-6 shrink-0 text-[#25D366]" />
                <span>
                  <span className="block font-display text-lg font-bold">WhatsApp {WA_NUMBER_DISPLAY}</span>
                  <span className="block text-sm text-white/50">Fastest way to reach us</span>
                </span>
              </a>
            </li>
            <li>
              <a
                href={`tel:+${WA_NUMBER}`}
                onClick={() => track("call_click", { location: "contact_page" })}
                className="flex items-center gap-4 rounded-2xl border border-white/10 p-5 transition-colors hover:bg-white/5"
                data-testid="contact-call"
              >
                <Phone className="h-6 w-6 shrink-0 text-ember" />
                <span>
                  <span className="block font-display text-lg font-bold">Call {WA_NUMBER_DISPLAY}</span>
                  <span className="block text-sm text-white/50">India number; international calls welcome</span>
                </span>
              </a>
            </li>
            <li className="flex items-center gap-4 rounded-2xl border border-white/10 p-5">
              <Globe2 className="h-6 w-6 shrink-0 text-ember" />
              <span>
                <span className="block font-display text-lg font-bold">India · USA · UAE</span>
                <span className="block text-sm text-white/50">
                  We work remotely with businesses in all three. See{" "}
                  <Link to="/pricing/" className="text-ember underline-offset-2 hover:underline">pricing</Link>.
                </span>
              </span>
            </li>
          </ul>
        </div>
        <div className="rounded-3xl border border-white/10 bg-ink p-6 sm:p-8">
          <h2 className="font-display text-2xl font-bold tracking-tight">Get a free website plan</h2>
          <p className="mt-2 text-sm text-white/50">Your details open a WhatsApp chat with us, so nothing is stored on this site.</p>
          <div className="mt-6">
            {sent ? (
              <div className="rounded-2xl bg-ember/10 p-6 text-sm text-white/80" data-testid="contact-sent">
                Thanks, {sent.form.name}. WhatsApp should have opened with your details.{" "}
                <a href={sent.link} target="_blank" rel="noopener noreferrer" className="font-semibold text-ember underline">Open WhatsApp again</a>
              </div>
            ) : (
              <LeadForm
                extended
                source="contact_page"
                testIdPrefix="contact-form"
                submitLabel="Send on WhatsApp"
                onSubmitted={(form, link) => {
                  setSent({ form, link });
                  window.open(link, "_blank", "noopener");
                }}
              />
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
